import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, LayoutDashboard, Users, Sparkles, Briefcase, ShieldCheck, Settings, Activity, FilePlus2, KeyRound, UserCog, Menu, X, Megaphone, Clapperboard, Building2 } from "lucide-react";

const ROLE_NAV = {
    brand: [
        { to: "/brand/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "side-brand-dashboard" },
        { to: "/brand/campaigns", label: "Campaigns", icon: Briefcase, testid: "side-brand-campaigns" },
        { to: "/brand/campaigns/new", label: "New campaign", icon: FilePlus2, testid: "side-brand-new-campaign" },
        { to: "/brand/roster", label: "Roster", icon: Users, testid: "side-brand-roster" },
        { to: "/brand/membership", label: "Plan & Videos", icon: Settings, testid: "side-brand-membership" },
        { to: "/brand/code", label: "Access code", icon: KeyRound, testid: "side-brand-code" },
    ],
    creator: [
        { to: "/creator/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "side-creator-dashboard" },
        { to: "/creator/jobs", label: "Jobs board", icon: Megaphone, testid: "side-creator-jobs" },
        { to: "/creator/onboarding", label: "Profile", icon: Sparkles, testid: "side-creator-profile" },
    ],
    admin: [
        { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "side-admin-dashboard" },
        { to: "/admin/vetting", label: "Vetting Queue", icon: ShieldCheck, testid: "side-admin-vetting" },
        { to: "/admin/campaigns", label: "Campaigns", icon: Clapperboard, testid: "side-admin-campaigns" },
        { to: "/admin/jobs", label: "Jobs", icon: Megaphone, testid: "side-admin-jobs" },
        { to: "/admin/matching", label: "Matching", icon: Activity, testid: "side-admin-matching" },
        { to: "/admin/creators", label: "Creators", icon: Users, testid: "side-admin-creators" },
        { to: "/admin/brands", label: "Brands", icon: Building2, testid: "side-admin-brands" },
        { to: "/admin/users", label: "Users", icon: UserCog, testid: "side-admin-users" },
        { to: "/admin/brand-access", label: "Brand Access", icon: Briefcase, testid: "side-admin-brand-access" },
        { to: "/admin/leads", label: "Audit Leads", icon: Sparkles, testid: "side-admin-leads" },
        { to: "/admin/demo", label: "Demo Access", icon: KeyRound, testid: "side-admin-demo" },
    ],
};

export const DashboardShell = ({ children, title, subtitle, role, actions }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const items = ROLE_NAV[role || user?.role] || [];
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close the drawer whenever the route changes
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const NavLinks = ({ onNavigate }) => (
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {items.map((it) => {
                const Icon = it.icon;
                const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
                return (
                    <Link key={it.to} to={it.to} data-testid={it.testid} onClick={onNavigate} className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${active ? "bg-yc-blue text-white border-yc-ink shadow-yc-sm" : "border-transparent hover:border-yc-ink hover:bg-yc-cream"}`}>
                        <Icon className="w-4 h-4" />
                        <span className="font-semibold text-sm">{it.label}</span>
                    </Link>
                );
            })}
        </nav>
    );

    return (
        <div className="flex min-h-screen bg-yc-cream">
            <aside className="w-64 border-r-2 border-yc-ink bg-white hidden md:flex flex-col">
                <div className="px-6 py-5 border-b-2 border-yc-ink">
                    <div onClick={() => navigate("/")} className="cursor-pointer font-heading font-black text-xl tracking-tighter flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-yc-pink border-2 border-yc-ink" /> CURATED CHAOS
                    </div>
                    <div className="yc-overline mt-3">{(role || user?.role) || ""}</div>
                </div>
                <NavLinks />
                <div className="p-4 border-t-2 border-yc-ink">
                    <div className="text-xs text-yc-ink/60 mb-2 truncate">{user?.email}</div>
                    <button data-testid="side-logout-btn" onClick={logout} className="yc-btn-secondary w-full text-sm py-2 px-3">
                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav-drawer">
                    <div className="absolute inset-0 bg-yc-ink/50" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-overlay" />
                    <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white border-r-2 border-yc-ink flex flex-col shadow-yc-sm">
                        <div className="px-5 py-4 border-b-2 border-yc-ink flex items-center justify-between">
                            <div onClick={() => { setMobileOpen(false); navigate("/"); }} className="cursor-pointer font-heading font-black text-lg tracking-tighter flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-full bg-yc-pink border-2 border-yc-ink" /> CURATED CHAOS
                            </div>
                            <button data-testid="mobile-nav-close" onClick={() => setMobileOpen(false)} className="p-2 rounded-xl border-2 border-yc-ink bg-white" aria-label="Close menu">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-5 pt-3"><div className="yc-overline">{(role || user?.role) || ""}</div></div>
                        <NavLinks onNavigate={() => setMobileOpen(false)} />
                        <div className="p-4 border-t-2 border-yc-ink">
                            <div className="text-xs text-yc-ink/60 mb-2 truncate">{user?.email}</div>
                            <button data-testid="mobile-logout-btn" onClick={logout} className="yc-btn-secondary w-full text-sm py-2 px-3">
                                <LogOut className="w-4 h-4 mr-2" /> Sign out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main className="flex-1 flex flex-col overflow-hidden min-w-0">
                <header className="h-16 border-b-2 border-yc-ink bg-white flex items-center justify-between gap-2 px-4 md:px-6">
                    <div className="flex items-center gap-3 min-w-0">
                        <button data-testid="mobile-nav-toggle" onClick={() => setMobileOpen(true)} className="md:hidden p-2 rounded-xl border-2 border-yc-ink bg-white shrink-0" aria-label="Open menu">
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="min-w-0">
                            <h1 className="font-heading font-bold text-lg md:text-xl tracking-tight truncate" data-testid="dashboard-title">{title}</h1>
                            {subtitle && <p className="text-xs text-yc-ink/60 truncate hidden sm:block">{subtitle}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">{actions}</div>
                </header>
                <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-8">{children}</div>
            </main>
        </div>
    );
};
